<template>
  <div class="fill" :class="[fsClass, { 'has-bg': hasCustomBg, 'is-dark': isDarkBg }]">
    <div v-if="hasCustomBg" class="bg-layer" :style="bgStyle"></div>
    <div v-if="loading" class="state">
      <div class="spinner"></div>
      <p>正在加载商品…</p>
    </div>
    <div v-else-if="error" class="state error">
      <div class="state-icon">!</div>
      <p>{{ error }}</p>
    </div>
    <div v-else-if="done" class="state success">
      <div class="ok-badge">✓</div>
      <h2>提交成功</h2>
      <p>{{ doneMessage }}</p>
      <button type="button" class="ghost-btn" @click="resetPage">返回继续看看</button>
    </div>

    <template v-else>
      <header class="hero">
        <div class="hero__badge">订购表单</div>
        <h1 class="hero__title">{{ form.title }}</h1>
        <p v-if="form.description" class="hero__desc">{{ form.description }}</p>
        <div class="fontbar">
          <span class="fontbar__label">字号</span>
          <button
            v-for="o in fontOptions"
            :key="o.value"
            type="button"
            class="fontbar__btn"
            :class="{ on: fontSizeMode === o.value }"
            @click="setFontSizeMode(o.value)"
          >{{ o.label }}</button>
        </div>
      </header>

      <div v-if="banners.length" class="banner">
        <div class="banner__track" ref="bannerTrack" @scroll.passive="onBannerScroll">
          <div
            v-for="(b, i) in banners"
            :key="b.id"
            class="banner__item"
            :class="{ 'is-link': b.link_type !== 'none' }"
            @click="tapBanner(b)"
          >
            <img :src="b.image" :alt="b.title || `轮播图${i + 1}`" />
            <span v-if="b.title" class="banner__title">{{ b.title }}</span>
          </div>
        </div>
        <div v-if="banners.length > 1" class="banner__dots">
          <span
            v-for="(b, i) in banners"
            :key="b.id"
            class="banner__dot"
            :class="{ on: i === bannerIndex }"
            @click="goBanner(i)"
          ></span>
        </div>
      </div>

      <div class="sticky">
        <div class="search">
          <span class="search__icon"></span>
          <input
            v-model.trim="keyword"
            type="search"
            placeholder="搜名称 / 成分 / 主治，支持拼音"
            enterkeyhint="search"
          />
          <button v-if="keyword" type="button" class="search__clear" @click="keyword = ''">×</button>
        </div>
      </div>

      <nav v-if="categories.length > 1" class="cats" aria-label="商品分类">
        <button
          type="button"
          class="chip"
          :class="{ on: selectedCategoryId === null }"
          @click="selectCategory(null)"
        >全部</button>
        <button
          v-for="cat in categories"
          :key="cat.id"
          type="button"
          class="chip"
          :class="{ on: selectedCategoryId === cat.id }"
          @click="selectCategory(cat.id)"
        >{{ cat.name }}</button>
      </nav>

      <div id="cat-list" class="body">
        <section v-for="cat in visibleCategories" :key="cat.id" :id="'cat-' + cat.id" class="category">
          <h2 class="category__title">{{ cat.name }}</h2>
          <div class="list">
            <article
              v-for="p in cat.products"
              :key="p.id"
              class="item"
              :class="{ 'in-cart': productQty(p) > 0 }"
              @click="openDetail(p)"
            >
              <div v-if="showImages && p.cover" class="item__cover" :style="{ backgroundImage: 'url(' + p.cover + ')' }"></div>
              <div class="item__main">
                <div class="item__name">
                  {{ p.name }}
                  <span v-if="p.has_multi_spec" class="spec-tag">{{ (p.specs || []).length }} 规格</span>
                </div>
                <div v-if="p.ingredients || p.control_targets" class="item__meta">
                  <div v-if="p.ingredients" class="meta">
                    <span class="meta__k">成分</span>
                    <span class="meta__v">{{ p.ingredients }}</span>
                  </div>
                  <div v-if="p.control_targets" class="meta meta--target">
                    <span class="meta__k">主要防治</span>
                    <span class="meta__v">{{ p.control_targets }}</span>
                  </div>
                </div>
                <div class="item__foot">
                  <div class="price">
                    <span class="now">¥{{ money(p.min_price) }}</span>
                    <span v-if="p.has_multi_spec" class="qi">起</span>
                    <span v-if="p.min_original_price != null" class="old">¥{{ money(p.min_original_price) }}</span>
                  </div>
                  <div class="stock">库存 {{ p.total_stock || 0 }}{{ unitOf(p) }}</div>
                </div>
              </div>
              <div class="item__act" @click.stop>
                <div v-if="!p.has_multi_spec && productQty(p) > 0" class="stepper">
                  <button type="button" class="stepper__btn" @click="bumpProduct(p, -1)">−</button>
                  <span class="stepper__n">{{ productQty(p) }}</span>
                  <button type="button" class="stepper__btn plus" @click="bumpProduct(p, 1)">+</button>
                </div>
                <button v-else type="button" class="add" @click="onAddClick(p)">
                  <span v-if="productQty(p) > 0" class="add__badge">{{ productQty(p) }}</span>
                  +
                </button>
              </div>
            </article>
          </div>
        </section>
        <div v-if="visibleCategories.length === 0" class="empty">没有找到相关商品</div>
      </div>

      <div class="cart-bar" :class="{ show: cartCount > 0 }">
        <button type="button" class="cart-bar__left" @click="openCheckout">
          <span class="cart-ico">
            <span v-if="cartCount" class="cart-ico__n">{{ cartCount }}</span>
          </span>
          <span class="cart-bar__sum">
            <em>¥{{ money(cartAmount) }}</em>
            <small>已选 {{ cartCount }} 件</small>
          </span>
        </button>
        <button type="button" class="cart-bar__go" @click="openCheckout">去结算</button>
      </div>
    </template>

    <!-- 详情 -->
    <div v-if="detailProduct" class="mask" @click.self="detailProduct = null">
      <div class="sheet">
        <div class="sheet__handle"></div>
        <div class="sheet__title">{{ detailProduct.name }}</div>
        <div v-if="showImages && detailProduct.cover" class="sheet__cover" :style="{ backgroundImage: 'url(' + detailProduct.cover + ')' }"></div>
        <div class="sheet__row" v-if="detailProduct.ingredients"><b>成分</b>{{ detailProduct.ingredients }}</div>
        <div class="sheet__row" v-if="detailProduct.control_targets"><b>主要防治</b>{{ detailProduct.control_targets }}</div>
        <div class="sheet__row" v-if="detailProduct.dosage"><b>使用量</b>{{ detailProduct.dosage }}</div>
        <div class="sheet__row" v-if="detailProduct.detail"><b>说明</b>{{ detailProduct.detail }}</div>
        <button type="button" class="sheet__ok" @click="fromDetailAdd">{{ detailProduct.has_multi_spec ? '选规格加入' : '加入订购' }}</button>
      </div>
    </div>

    <!-- 选规格 -->
    <div v-if="addProduct" class="mask" @click.self="addProduct = null">
      <div class="sheet">
        <div class="sheet__handle"></div>
        <div class="sheet__title">{{ addProduct.name }}</div>
        <button
          v-for="s in addProduct.specs || []"
          :key="s.id"
          type="button"
          class="spec"
          :class="{ on: selectedSpec && selectedSpec.id === s.id, sold: isSoldOut(s) }"
          :disabled="isSoldOut(s)"
          @click="selectedSpec = s"
        >
          <div class="spec__name">{{ s.name }}<span v-if="s.unit" class="spec__unit"> / {{ s.unit }}</span></div>
          <div class="spec__meta">
            <span class="now">¥{{ money(s.price) }}</span>
            <span v-if="s.original_price != null" class="old">¥{{ money(s.original_price) }}</span>
            <span class="stock">库存 {{ s.stock }}</span>
          </div>
        </button>
        <div class="qty-row">
          <span>数量</span>
          <div class="stepper lg">
            <button type="button" class="stepper__btn" @click="qty = Math.max(1, qty - 1)">−</button>
            <input class="stepper__input" v-model.number="qty" type="number" min="1" />
            <button type="button" class="stepper__btn plus" @click="qty = qty + 1">+</button>
          </div>
        </div>
        <button type="button" class="sheet__ok" @click="confirmAdd">加入订购 · ¥{{ money(addLineAmount) }}</button>
      </div>
    </div>

    <!-- 结算 -->
    <div v-if="checkoutOpen" class="mask" @click.self="checkoutOpen = false">
      <div class="sheet checkout">
        <div class="sheet__handle"></div>
        <div class="sheet__title">确认订购</div>
        <div class="cart-lines">
          <div v-for="row in cartRows" :key="row.spec.id" class="line">
            <div class="line__info">
              <div class="line__name">{{ row.product.name }}</div>
              <div class="line__spec">{{ row.spec.name }} · ¥{{ money(row.spec.price) }}</div>
            </div>
            <div class="stepper">
              <button type="button" class="stepper__btn" @click="setSpecQty(row.spec, row.product, row.quantity - 1)">−</button>
              <span class="stepper__n">{{ row.quantity }}</span>
              <button type="button" class="stepper__btn plus" @click="setSpecQty(row.spec, row.product, row.quantity + 1)">+</button>
            </div>
          </div>
        </div>
        <div class="field">
          <label><span class="req">*</span>姓名</label>
          <input v-model.trim="customer.name" type="text" placeholder="请输入姓名" />
        </div>
        <div class="field">
          <label><span class="req">*</span>手机</label>
          <input v-model.trim="customer.phone" type="tel" maxlength="11" placeholder="请输入手机号" />
        </div>
        <div class="field">
          <label>备注</label>
          <input v-model.trim="customer.remark" type="text" placeholder="选填，如收货备注" />
        </div>
        <button type="button" class="sheet__ok" :disabled="submitting" @click="submit">
          {{ submitting ? '提交中…' : '提交订购 · ¥' + money(cartAmount) }}
        </button>
      </div>
    </div>

    <div v-if="toast" class="toast">{{ toast }}</div>
  </div>
</template>

<script>
import { publicOrderForm, publicOrderFormSubmit } from '@/api/orderForm'

const FONT_SIZE_KEY = 'order_form_font_size'
const FONT_SIZES = ['standard', 'medium', 'large']

// 访客自己选过就一直用他选的，没选过时跟随表单配置的默认字号
function readFontSizeMode() {
  try {
    const saved = window.localStorage.getItem(FONT_SIZE_KEY)
    if (FONT_SIZES.indexOf(saved) !== -1) return saved
  } catch (e) {
    // 隐私模式下 localStorage 不可用
  }
  return ''
}

export default {
  name: 'OrderFormFillPage',
  data() {
    return {
      fontSizeMode: readFontSizeMode() || 'standard',
      fontSizeChosen: !!readFontSizeMode(),
      fontOptions: [
        { value: 'standard', label: '标准' },
        { value: 'medium', label: '大' },
        { value: 'large', label: '超大' },
      ],
      loading: true,
      error: '',
      form: {},
      keyword: '',
      selectedCategoryId: null,
      customer: { name: '', phone: '', remark: '' },
      cart: {},
      detailProduct: null,
      addProduct: null,
      selectedSpec: null,
      qty: 1,
      checkoutOpen: false,
      submitting: false,
      done: false,
      doneMessage: '',
      toast: '',
      toastTimer: null,
      bannerIndex: 0,
      bannerTimer: null,
    }
  },
  computed: {
    fsClass() {
      return this.fontSizeMode === 'standard' ? '' : `fs-${this.fontSizeMode}`
    },
    sheetOpen() {
      return !!(this.detailProduct || this.addProduct || this.checkoutOpen)
    },
    categories() {
      return this.form.categories || []
    },
    banners() {
      return (this.form.banners || []).filter((b) => b && b.image)
    },
    showImages() {
      return !!this.form.show_product_image
    },
    hasCustomBg() {
      const t = this.form.bg_type
      if (t === 'gradient') return !!(this.form.bg_color_start || this.form.bg_color_end)
      if (t === 'image') return !!this.form.bg_image
      return false
    },
    isDarkBg() {
      return this.hasCustomBg && !!this.form.bg_dark
    },
    bgStyle() {
      if (this.form.bg_type === 'image') {
        return {
          backgroundImage: `url(${this.form.bg_image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }
      }
      const start = this.form.bg_color_start || '#f6f7f9'
      const end = this.form.bg_color_end || '#f6f7f9'
      return { backgroundImage: `linear-gradient(180deg, ${start}, ${end})` }
    },
    keywordTokens() {
      return (this.keyword || '').toLowerCase().split(/\s+/).filter(Boolean)
    },
    visibleCategories() {
      const tokens = this.keywordTokens
      return this.categories
        .map((c) => {
          const products = (c.products || []).filter((p) => this.matchProduct(p, c, tokens))
          return { id: c.id, name: c.name, products }
        })
        .filter((c) => c.products.length)
    },
    cartRows() {
      return Object.values(this.cart).filter((row) => row && row.quantity > 0)
    },
    cartCount() {
      return this.cartRows.reduce((n, row) => n + (row.quantity || 0), 0)
    },
    cartAmount() {
      return this.cartRows.reduce((n, row) => n + Number(row.spec.price || 0) * (row.quantity || 0), 0)
    },
    addLineAmount() {
      if (!this.selectedSpec) return 0
      return Number(this.selectedSpec.price || 0) * (Number(this.qty) || 0)
    },
  },
  watch: {
    sheetOpen(v) {
      document.body.style.overflow = v ? 'hidden' : ''
    },
  },
  methods: {
    setFontSizeMode(value) {
      if (FONT_SIZES.indexOf(value) === -1) return
      this.fontSizeMode = value
      this.fontSizeChosen = true
      try {
        window.localStorage.setItem(FONT_SIZE_KEY, value)
      } catch (e) {
        // 存不下就只在本次访问生效
      }
    },
    money(v) {
      if (v == null || v === '') return '0.00'
      return Number(v).toFixed(2)
    },
    // search_key 由后端拼好：原文 + 全拼 + 首字母；老数据缺字段时退回本地拼接
    searchHay(p, cat) {
      if (p.search_key) return p.search_key
      return [
        p.name, p.ingredients, p.control_targets, p.dosage, p.detail,
        cat && cat.name,
        ...(p.specs || []).map((s) => s.name),
      ].filter(Boolean).join(' ').toLowerCase()
    },
    matchProduct(p, cat, tokens) {
      if (!tokens.length) return true
      const hay = this.searchHay(p, cat)
      return tokens.every((t) => hay.indexOf(t) !== -1)
    },
    findProduct(id) {
      for (const cat of this.categories) {
        const hit = (cat.products || []).find((p) => p.id === id)
        if (hit) return hit
      }
      return null
    },
    tapBanner(b) {
      if (!b || b.link_type === 'none') return
      if (b.link_type === 'product') {
        const product = this.findProduct(b.product)
        if (product) this.openDetail(product)
        else this.showToast('该产品已下架')
        return
      }
      if (b.link_type === 'category') {
        this.keyword = ''
        this.selectCategory(b.category)
      }
    },
    goBanner(i) {
      const track = this.$refs.bannerTrack
      if (!track) return
      track.scrollTo({ left: track.clientWidth * i, behavior: 'smooth' })
      this.bannerIndex = i
    },
    onBannerScroll() {
      const track = this.$refs.bannerTrack
      if (!track || !track.clientWidth) return
      this.bannerIndex = Math.round(track.scrollLeft / track.clientWidth)
    },
    startBannerAuto() {
      this.stopBannerAuto()
      if (this.banners.length < 2) return
      this.bannerTimer = setInterval(() => {
        if (this.sheetOpen) return
        this.goBanner((this.bannerIndex + 1) % this.banners.length)
      }, 4000)
    },
    stopBannerAuto() {
      clearInterval(this.bannerTimer)
      this.bannerTimer = null
    },
    unitOf(p) {
      const s = (p && p.specs && p.specs[0]) || {}
      return s.unit || '件'
    },
    isSoldOut(s) {
      return s && s.stock != null && Number(s.stock) <= 0
    },
    productQty(p) {
      const specs = (p && p.specs) || []
      return specs.reduce((n, s) => n + ((this.cart[s.id] && this.cart[s.id].quantity) || 0), 0)
    },
    singleSpec(p) {
      const specs = (p && p.specs) || []
      return specs.length === 1 ? specs[0] : null
    },
    showToast(msg) {
      this.toast = msg
      clearTimeout(this.toastTimer)
      this.toastTimer = setTimeout(() => {
        this.toast = ''
      }, 1800)
    },
    selectCategory(id) {
      this.selectedCategoryId = id
      if (this.keyword) return
      this.$nextTick(() => {
        const el = document.getElementById(id == null ? 'cat-list' : 'cat-' + id)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },
    onAddClick(p) {
      const one = this.singleSpec(p)
      if (one) {
        this.setSpecQty(one, p, this.productQty(p) + 1)
        return
      }
      this.openAdd(p)
    },
    bumpProduct(p, delta) {
      const one = this.singleSpec(p)
      if (!one) {
        this.openAdd(p)
        return
      }
      this.setSpecQty(one, p, this.productQty(p) + delta)
    },
    setSpecQty(spec, product, qty) {
      if (!spec) return false
      const next = Number(qty) || 0
      if (next <= 0) {
        this.$delete(this.cart, spec.id)
        if (!this.cartCount) this.checkoutOpen = false
        return true
      }
      if (spec.stock != null && next > Number(spec.stock)) {
        this.showToast('库存不足')
        return false
      }
      this.$set(this.cart, spec.id, { product, spec, quantity: next })
      return true
    },
    openDetail(p) {
      this.detailProduct = p
    },
    fromDetailAdd() {
      const p = this.detailProduct
      this.detailProduct = null
      if (p) this.onAddClick(p)
    },
    openAdd(p) {
      const specs = (p.specs || []).filter((s) => s && s.is_active !== false)
      if (!specs.length) {
        this.showToast('暂无规格')
        return
      }
      this.addProduct = p
      this.selectedSpec = specs.find((s) => !this.isSoldOut(s)) || specs[0]
      const existing = this.selectedSpec && this.cart[this.selectedSpec.id]
      this.qty = existing ? existing.quantity : 1
    },
    confirmAdd() {
      if (!this.selectedSpec) {
        this.showToast('请选择规格')
        return
      }
      const qty = Number(this.qty) || 0
      if (qty < 1) {
        this.showToast('数量至少为 1')
        return
      }
      if (!this.setSpecQty(this.selectedSpec, this.addProduct, qty)) return
      this.addProduct = null
      this.showToast('已加入')
    },
    openCheckout() {
      if (!this.cartCount) return
      this.checkoutOpen = true
    },
    async load() {
      const slug = this.$route.params.slug
      if (!slug) {
        this.error = '缺少表单标识'
        this.loading = false
        return
      }
      this.loading = true
      this.error = ''
      try {
        this.form = (await publicOrderForm(slug)) || {}
        if (!this.fontSizeChosen && FONT_SIZES.indexOf(this.form.font_size) !== -1) {
          this.fontSizeMode = this.form.font_size
        }
        document.title = this.form.title || '订购表单'
        this.bannerIndex = 0
        this.$nextTick(this.startBannerAuto)
      } catch (e) {
        const detail = e.response && e.response.data && e.response.data.detail
        this.error = detail || '表单不存在或已停用'
      } finally {
        this.loading = false
      }
    },
    async submit() {
      if (this.submitting) return
      if (!this.customer.name) {
        this.showToast('请填写姓名')
        return
      }
      if (!this.customer.phone || String(this.customer.phone).length < 7) {
        this.showToast('请填写正确手机号')
        return
      }
      if (!this.cartCount) {
        this.showToast('请先选择商品')
        return
      }
      this.submitting = true
      try {
        const items = this.cartRows.map((row) => ({
          spec_id: row.spec.id,
          quantity: row.quantity,
        }))
        const res = await publicOrderFormSubmit(this.$route.params.slug, {
          customer_name: this.customer.name,
          customer_phone: this.customer.phone,
          remark: this.customer.remark || '',
          items,
        })
        this.checkoutOpen = false
        this.done = true
        this.doneMessage = (res && res.message) || '我们会尽快联系您确认订单'
        this.cart = {}
      } catch (e) {
        const detail = e.response && e.response.data && e.response.data.detail
        this.showToast(detail || '提交失败，请稍后重试')
      } finally {
        this.submitting = false
      }
    },
    resetPage() {
      this.done = false
      this.doneMessage = ''
      this.customer = { name: '', phone: '', remark: '' }
    },
  },
  mounted() {
    this.load()
  },
  beforeDestroy() {
    document.body.style.overflow = ''
    clearTimeout(this.toastTimer)
    this.stopBannerAuto()
  },
}
</script>

<style scoped>
.fill {
  --ink: #101828;
  --ink-2: #475467;
  --ink-3: #98a2b3;
  --line: #eaecf0;
  --bg: #f6f7f9;
  --brand: #0d9f6e;
  --brand-deep: #0b8a5f;
  --brand-soft: #e7f7f0;
  --price: #f04438;
  /* 三档字号：标准 / 大 / 超大，页面所有字号按此系数缩放 */
  --fs: 1;

  position: relative;
  min-height: 100vh;
  max-width: 560px;
  margin: 0 auto;
  background: var(--bg);
  color: var(--ink);
  font-size: calc(14px * var(--fs));
  font-family: "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
  padding-bottom: 88px;
}
.fill.fs-medium { --fs: 1.2; }
.fill.fs-large { --fs: 1.5; }
.fill.has-bg { background: transparent; }
.bg-layer {
  position: fixed;
  inset: 0;
  z-index: -1;
  background-repeat: no-repeat;
  background-attachment: fixed;
}

/* 自定义背景时，头图与吸顶条让位给背景 */
.has-bg .hero {
  background: transparent;
  border-bottom: 0;
}
.has-bg .sticky {
  background: transparent;
  backdrop-filter: none;
}
.has-bg .search {
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(8px);
}
.is-dark .hero__title,
.is-dark .category__title,
.is-dark .state,
.is-dark .empty {
  color: #fff;
}
.is-dark .hero__desc { color: rgba(255, 255, 255, 0.8); }
.is-dark .hero__badge {
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
}
.is-dark .chip {
  background: rgba(255, 255, 255, 0.14);
  border-color: rgba(255, 255, 255, 0.24);
  color: #fff;
}
.is-dark .chip.on {
  background: #fff;
  border-color: #fff;
  color: var(--ink);
}
.state {
  min-height: 70vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 24px;
  text-align: center;
  color: var(--ink-2);
}
.state.error { color: var(--price); }
.state-icon, .ok-badge {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: calc(28px * var(--fs));
  font-weight: 700;
}
.state-icon { background: #fef3f2; color: var(--price); }
.ok-badge { background: var(--brand-soft); color: var(--brand); }
.success h2 { margin: 0; font-size: calc(22px * var(--fs)); color: var(--ink); }
.success p { margin: 0; color: var(--ink-2); line-height: 1.6; }
.spinner {
  width: 28px;
  height: 28px;
  border: 3px solid var(--line);
  border-top-color: var(--brand);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.ghost-btn {
  margin-top: 8px;
  border: 1px solid var(--line);
  background: #fff;
  color: var(--ink);
  border-radius: 999px;
  padding: 10px 22px;
  font-size: calc(14px * var(--fs));
  font-weight: 600;
}

.hero {
  padding: 26px 18px 20px;
  background:
    radial-gradient(90% 120% at 100% -30%, rgba(13, 159, 110, 0.12), transparent 60%),
    #fff;
  border-bottom: 1px solid var(--line);
}
.hero__badge {
  display: inline-block;
  font-size: calc(11px * var(--fs));
  letter-spacing: 1.5px;
  padding: 3px 9px;
  border-radius: 999px;
  background: var(--brand-soft);
  color: var(--brand-deep);
  font-weight: 700;
  margin-bottom: 10px;
}
.hero__title {
  margin: 0;
  font-size: calc(24px * var(--fs));
  font-weight: 800;
  letter-spacing: -0.4px;
  line-height: 1.3;
  color: var(--ink);
}
.hero__desc {
  margin: 8px 0 0;
  font-size: calc(13px * var(--fs));
  line-height: 1.6;
  color: var(--ink-2);
}

/* 字号切换器自身固定大小，不跟随 --fs 缩放 */
.fontbar {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
}
.fontbar__label {
  font-size: 12px;
  color: var(--ink-3);
}
.fontbar__btn {
  border: 1px solid var(--line);
  background: #fff;
  color: var(--ink-2);
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12px;
  cursor: pointer;
}
.fontbar__btn.on {
  background: var(--ink);
  border-color: var(--ink);
  color: #fff;
}
.is-dark .fontbar__label { color: rgba(255, 255, 255, 0.7); }
.is-dark .fontbar__btn {
  background: rgba(255, 255, 255, 0.14);
  border-color: rgba(255, 255, 255, 0.24);
  color: #fff;
}
.is-dark .fontbar__btn.on {
  background: #fff;
  border-color: #fff;
  color: var(--ink);
}

.banner {
  padding: 12px 12px 0;
}
.banner__track {
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  border-radius: 14px;
}
.banner__track::-webkit-scrollbar { display: none; }
.banner__item {
  position: relative;
  flex: 0 0 100%;
  scroll-snap-align: start;
  aspect-ratio: 2 / 1;
  background: #f2f4f7;
  overflow: hidden;
}
.banner__item.is-link { cursor: pointer; }
.banner__item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.banner__title {
  position: absolute;
  left: 12px;
  bottom: 10px;
  padding: 4px 10px;
  border-radius: 8px;
  background: rgba(16, 24, 40, 0.55);
  color: #fff;
  font-size: calc(13px * var(--fs));
  font-weight: 600;
}
.banner__dots {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 8px;
}
.banner__dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: var(--ink-3);
  opacity: 0.4;
  cursor: pointer;
  transition: width 0.2s, opacity 0.2s;
}
.banner__dot.on {
  width: 16px;
  opacity: 1;
  background: var(--brand);
}
.is-dark .banner__dot { background: #fff; }

.sticky {
  position: sticky;
  top: 0;
  z-index: 12;
  background: rgba(246, 247, 249, 0.86);
  backdrop-filter: blur(12px);
  padding: 10px 12px 8px;
}
.search {
  display: flex;
  align-items: center;
  height: calc(40px * var(--fs));
  padding: 0 12px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 10px;
}
.search__icon {
  width: 16px;
  height: 16px;
  margin-right: 8px;
  opacity: 0.55;
  background: center / contain no-repeat url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' stroke='%2398a2b3' stroke-width='2' viewBox='0 0 24 24'%3E%3Ccircle cx='11' cy='11' r='7'/%3E%3Cpath d='M20 20l-3-3'/%3E%3C/svg%3E");
}
.search input {
  flex: 1;
  border: 0;
  outline: 0;
  background: transparent;
  font-size: calc(14px * var(--fs));
  height: 100%;
  color: var(--ink);
}
.search__clear {
  border: 0;
  background: #f2f4f7;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  color: var(--ink-3);
  line-height: 20px;
}
.cats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px 14px 2px;
}
.chip {
  border: 1px solid var(--line);
  background: #fff;
  color: var(--ink-2);
  border-radius: 999px;
  padding: 6px 14px;
  font-size: calc(13px * var(--fs));
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.chip.on {
  background: var(--ink);
  border-color: var(--ink);
  color: #fff;
}

.body { padding: 4px 12px 16px; }
.category { scroll-margin-top: 72px; }
.category__title {
  margin: 16px 4px 10px;
  font-size: calc(15px * var(--fs));
  font-weight: 700;
  color: var(--ink);
}
.list { display: flex; flex-direction: column; gap: 10px; }
.item {
  display: flex;
  gap: 12px;
  align-items: stretch;
  background: #fff;
  border-radius: 14px;
  padding: 14px;
  border: 1px solid var(--line);
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s, border-color 0.15s;
}
.item.in-cart {
  border-color: var(--brand);
  box-shadow: 0 0 0 3px var(--brand-soft);
}
.item:active { transform: scale(0.99); }
.item__cover {
  width: 72px;
  height: 72px;
  border-radius: 10px;
  background: #f2f4f7 center / cover no-repeat;
  flex-shrink: 0;
}
.item__main { flex: 1; min-width: 0; }
.item__name {
  font-size: calc(15px * var(--fs));
  font-weight: 700;
  line-height: 1.4;
  color: var(--ink);
}
.spec-tag {
  display: inline-block;
  margin-left: 6px;
  font-size: calc(11px * var(--fs));
  font-weight: 600;
  color: var(--brand-deep);
  background: var(--brand-soft);
  border-radius: 6px;
  padding: 1px 6px;
}
.item__meta {
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.meta {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.meta__k {
  flex: 0 0 auto;
  font-size: calc(11px * var(--fs));
  line-height: calc(18px * var(--fs));
  padding: 0 6px;
  border-radius: 4px;
  background: #f2f4f7;
  color: var(--ink-3);
}
.meta--target .meta__k {
  background: var(--brand-soft);
  color: var(--brand-deep);
}
.meta__v {
  flex: 1;
  min-width: 0;
  font-size: calc(12px * var(--fs));
  color: var(--ink-2);
  line-height: 1.45;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.item__foot {
  margin-top: 8px;
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}
.price { display: flex; align-items: baseline; gap: 4px; }
.now { color: var(--price); font-size: calc(19px * var(--fs)); font-weight: 800; letter-spacing: -0.4px; }
.qi { font-size: calc(12px * var(--fs)); color: var(--price); font-weight: 600; }
.old { color: var(--ink-3); font-size: calc(12px * var(--fs)); text-decoration: line-through; }
.stock { font-size: calc(12px * var(--fs)); color: var(--ink-3); }
.item__act {
  display: flex;
  align-items: flex-end;
  flex-shrink: 0;
}
.add {
  position: relative;
  width: calc(32px * var(--fs));
  height: calc(32px * var(--fs));
  border: 0;
  border-radius: 10px;
  background: var(--ink);
  color: #fff;
  font-size: calc(20px * var(--fs));
  line-height: calc(30px * var(--fs));
  cursor: pointer;
}
.add__badge {
  position: absolute;
  top: -7px;
  right: -7px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: var(--price);
  color: #fff;
  font-size: calc(10px * var(--fs));
  line-height: 16px;
  font-weight: 700;
}
.stepper {
  display: flex;
  align-items: center;
  gap: 4px;
}
.stepper.lg { border: 1px solid var(--line); border-radius: 10px; overflow: hidden; gap: 0; }
.stepper__btn {
  width: calc(28px * var(--fs));
  height: calc(28px * var(--fs));
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #fff;
  color: var(--ink);
  font-size: calc(17px * var(--fs));
  line-height: calc(24px * var(--fs));
  cursor: pointer;
}
.stepper__btn.plus { background: var(--ink); border-color: var(--ink); color: #fff; }
.stepper.lg .stepper__btn { border: 0; border-radius: 0; width: 36px; height: 34px; background: #f9fafb; }
.stepper.lg .stepper__btn.plus { background: var(--ink); }
.stepper__n {
  min-width: 24px;
  text-align: center;
  font-weight: 700;
  font-size: calc(14px * var(--fs));
}
.stepper__input {
  width: 48px;
  height: 34px;
  border: 0;
  text-align: center;
  outline: none;
  font-weight: 700;
  color: var(--ink);
}
.empty {
  padding: 48px 16px;
  text-align: center;
  color: var(--ink-3);
}

.cart-bar {
  position: fixed;
  left: 50%;
  bottom: 12px;
  transform: translate(-50%, 120%);
  width: calc(100% - 24px);
  max-width: 536px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 8px 8px 14px;
  background: var(--ink);
  color: #fff;
  border-radius: 14px;
  box-shadow: 0 12px 28px rgba(16, 24, 40, 0.22);
  z-index: 15;
  opacity: 0;
  pointer-events: none;
  transition: transform 0.28s cubic-bezier(.2,.8,.2,1), opacity 0.2s;
}
.cart-bar.show {
  transform: translate(-50%, 0);
  opacity: 1;
  pointer-events: auto;
}
.cart-bar__left {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}
.cart-ico {
  position: relative;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: rgba(255,255,255,0.12) center / 18px no-repeat
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' stroke='%23fff' stroke-width='2' viewBox='0 0 24 24'%3E%3Cpath d='M6 7h15l-1.5 9h-12z'/%3E%3Cpath d='M6 7L5 4H2'/%3E%3C/svg%3E");
}
.cart-ico__n {
  position: absolute;
  top: -5px;
  right: -5px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: var(--price);
  font-size: calc(10px * var(--fs));
  line-height: 16px;
  text-align: center;
  font-weight: 700;
}
.cart-bar__sum { display: flex; flex-direction: column; }
.cart-bar__sum em { font-style: normal; font-size: calc(18px * var(--fs)); font-weight: 800; letter-spacing: -0.3px; }
.cart-bar__sum small { color: rgba(255,255,255,0.6); font-size: calc(11px * var(--fs)); }
.cart-bar__go {
  border: 0;
  background: var(--brand);
  color: #fff;
  border-radius: 10px;
  height: 40px;
  padding: 0 20px;
  font-size: calc(15px * var(--fs));
  font-weight: 700;
  cursor: pointer;
}

.mask {
  position: fixed;
  inset: 0;
  background: rgba(16, 24, 40, 0.45);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 30;
  animation: fadeIn 0.2s;
}
.sheet {
  width: 100%;
  max-width: 560px;
  background: #fff;
  border-radius: 20px 20px 0 0;
  padding: 10px 16px calc(16px + env(safe-area-inset-bottom));
  max-height: 86vh;
  overflow: auto;
  animation: slideUp 0.3s cubic-bezier(.2,.8,.2,1);
}
.sheet__handle {
  width: 36px;
  height: 4px;
  border-radius: 999px;
  background: var(--line);
  margin: 4px auto 12px;
}
.sheet__title { font-size: calc(17px * var(--fs)); font-weight: 700; margin-bottom: 12px; color: var(--ink); }
.sheet__cover {
  height: 140px;
  border-radius: 12px;
  background: #f2f4f7 center / cover no-repeat;
  margin-bottom: 12px;
}
.sheet__row {
  font-size: calc(13px * var(--fs));
  color: var(--ink-2);
  line-height: 1.65;
  margin-bottom: 10px;
}
.sheet__row b { display: block; color: var(--ink); margin-bottom: 2px; }
.sheet__ok {
  width: 100%;
  margin-top: 8px;
  border: 0;
  height: 46px;
  border-radius: 12px;
  background: var(--brand);
  color: #fff;
  font-size: calc(15px * var(--fs));
  font-weight: 700;
  cursor: pointer;
}
.sheet__ok:disabled { opacity: 0.6; }
.spec {
  display: block;
  width: 100%;
  text-align: left;
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 10px 12px;
  margin-bottom: 8px;
  background: #fff;
  cursor: pointer;
}
.spec.on { border-color: var(--brand); background: var(--brand-soft); }
.spec.sold { opacity: 0.45; }
.spec__name { font-weight: 600; margin-bottom: 4px; color: var(--ink); }
.spec__unit { color: var(--ink-3); font-weight: 400; }
.spec__meta { display: flex; gap: 8px; align-items: baseline; font-size: calc(13px * var(--fs)); }
.spec__meta .stock { color: var(--ink-3); margin-left: auto; }
.qty-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 12px 0;
}
.checkout .field { margin-bottom: 12px; }
.checkout .field label {
  display: block;
  font-size: calc(13px * var(--fs));
  font-weight: 600;
  margin-bottom: 6px;
  color: var(--ink-2);
}
.req { color: var(--price); margin-right: 4px; }
.checkout .field input {
  width: 100%;
  height: 42px;
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 0 12px;
  font-size: calc(15px * var(--fs));
  outline: none;
  box-sizing: border-box;
  background: #f9fafb;
  color: var(--ink);
}
.checkout .field input:focus {
  border-color: var(--brand);
  background: #fff;
  box-shadow: 0 0 0 3px var(--brand-soft);
}
.cart-lines { margin-bottom: 14px; }
.line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid var(--line);
}
.line__name { font-weight: 600; font-size: calc(14px * var(--fs)); color: var(--ink); }
.line__spec { font-size: calc(12px * var(--fs)); color: var(--ink-3); margin-top: 2px; }

.toast {
  position: fixed;
  left: 50%;
  bottom: 108px;
  transform: translateX(-50%);
  background: rgba(16, 24, 40, 0.92);
  color: #fff;
  padding: 10px 18px;
  border-radius: 10px;
  z-index: 80;
  font-size: calc(13px * var(--fs));
  pointer-events: none;
}

@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes slideUp { from { transform: translateY(24%); } to { transform: translateY(0); } }
</style>
